import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  LayoutGrid,
  List,
  Mail,
  MapPin,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import {
  archiverClients,
  creerClient,
  listerClients,
  lireClient,
  urlExportClients,
  type ClientResume,
  type Criteres,
  type FicheClient,
  type PageClients,
  type SensTri,
  type SituationClient,
  type TriClients,
} from '../api/clients';
import { BadgeSituation } from '../components/BadgeSituation';
import { BadgeStatut } from '../components/BadgeStatut';
import { FormulaireClient } from '../components/FormulaireClient';
import { Pagination } from '../components/Pagination';

const SITUATIONS: readonly SituationClient[] = ['en-cours', 'planifiee', 'sans-suite'];

const OPTIONS_SITUATION: ReadonlyArray<{ valeur: SituationClient | ''; libelle: string }> = [
  { valeur: '', libelle: 'Toutes les situations' },
  { valeur: 'en-cours', libelle: 'Intervention en cours' },
  { valeur: 'planifiee', libelle: 'Planifiée' },
  { valeur: 'sans-suite', libelle: 'Sans suite planifiée' },
];

/** Délai avant d'interroger le serveur pendant la frappe. */
const DELAI_RECHERCHE_MS = 300;

interface Etat {
  readonly donnees?: PageClients;
  readonly chargement: boolean;
  readonly erreur?: string;
  readonly pageDemandee?: number;
}

/** La fiche vient du serveur : elle a ses propres états, comme la liste. */
type Detail =
  | { readonly phase: 'ferme' }
  | { readonly phase: 'chargement'; readonly id: string }
  | { readonly phase: 'pret'; readonly fiche: FicheClient }
  | { readonly phase: 'panne'; readonly id: string; readonly message: string };

/** Deux initiales au plus, pour la pastille du client (comme dans le menu). */
function initiales(nom: string): string {
  const mots = nom.trim().split(/\s+/).filter(Boolean);
  return mots
    .slice(0, 2)
    .map((mot) => mot[0]?.toUpperCase() ?? '')
    .join('');
}

/** Une teinte stable par client, tirée de son identifiant : pas de hasard, donc
 *  la même pastille d'un écran à l'autre. */
function teinte(identifiant: string): string {
  const teintes = [
    'bg-blue-100 text-blue-700',
    'bg-orange-100 text-orange-700',
    'bg-purple-100 text-purple-700',
    'bg-emerald-100 text-emerald-700',
    'bg-slate-200 text-slate-700',
  ];
  const somme = [...identifiant].reduce((total, caractere) => total + caractere.charCodeAt(0), 0);
  return teintes[somme % teintes.length] ?? teintes[0]!;
}

export function Clients() {
  const [parametres, setParametres] = useSearchParams();

  // ── Critères : l'URL est la source unique (partage, Retour, rechargement).
  const recherche = parametres.get('recherche')?.trim() ?? '';
  const situation = (SITUATIONS.find((valeur) => valeur === parametres.get('situation')) ?? '') as
    SituationClient | '';
  const ville = parametres.get('ville') ?? '';
  const secteur = parametres.get('secteur') ?? '';
  const tri = (parametres.get('tri') ?? 'nom') as TriClients;
  const sens: SensTri = parametres.get('sens') === 'desc' ? 'desc' : 'asc';
  const pageBrute = Number.parseInt(parametres.get('page') ?? '1', 10);
  const page = Number.isFinite(pageBrute) && pageBrute > 0 ? pageBrute : 1;

  const ecrire = useCallback(
    (changements: Record<string, string>, remplacer = false) => {
      const suivants = new URLSearchParams(parametres);
      for (const [cle, valeur] of Object.entries(changements)) {
        if (valeur === '') suivants.delete(cle);
        else suivants.set(cle, valeur);
      }
      // Tout changement de critère ramène à la première page.
      if (!('page' in changements)) suivants.delete('page');
      setParametres(suivants, { replace: remplacer });
    },
    [parametres, setParametres],
  );

  const [saisie, setSaisie] = useState(recherche);
  const [etat, setEtat] = useState<Etat>({ chargement: true });
  const [detail, setDetail] = useState<Detail>({ phase: 'ferme' });
  const [creation, setCreation] = useState(false);
  const [vue, setVue] = useState<'tableau' | 'cartes'>('tableau');
  /** Les lignes cochées : vidées dès qu'un critère change. */
  const [selection, setSelection] = useState<readonly string[]>([]);
  const [confirmation, setConfirmation] = useState<string>();
  const [erreurAction, setErreurAction] = useState<string>();

  // Le champ suit l'URL : un Retour arrière doit remettre le texte d'avant.
  useEffect(() => {
    setSaisie(recherche);
  }, [recherche]);

  useEffect(() => {
    if (saisie === recherche) return;
    const minuteur = globalThis.setTimeout(
      () => ecrire({ recherche: saisie.trim() }, true),
      DELAI_RECHERCHE_MS,
    );
    return () => globalThis.clearTimeout(minuteur);
  }, [saisie, recherche, ecrire]);

  const charger = useCallback(
    async (signal: AbortSignal) => {
      setEtat((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
      const criteres: Criteres = { recherche, situation, ville, secteur, tri, sens, page };
      const resultat = await listerClients(criteres, { signal });
      if (resultat.ok && resultat.donnees) {
        setEtat({ donnees: resultat.donnees, chargement: false, pageDemandee: page });
      } else {
        setEtat((precedent) => ({
          ...precedent,
          chargement: false,
          erreur: resultat.message ?? 'Erreur inattendue.',
        }));
      }
    },
    [recherche, situation, ville, secteur, tri, sens, page],
  );

  useEffect(() => {
    const controleur = new AbortController();
    void charger(controleur.signal);
    return () => controleur.abort();
  }, [charger]);

  // Un changement de critère invalide la sélection précédente.
  useEffect(() => {
    setSelection([]);
  }, [recherche, situation, ville, secteur, tri, sens, page]);

  const donnees = etat.donnees;
  const filtres = donnees?.filtres;
  const secteursPossibles = donnees?.secteursPossibles ?? [];
  const filtresActifs = recherche !== '' || situation !== '' || ville !== '' || secteur !== '';

  // Le serveur ramène une page hors bornes à la dernière page réelle : l'URL doit
  // dire la même chose que l'écran.
  useEffect(() => {
    if (!donnees || etat.pageDemandee === undefined) return;
    if (donnees.page !== etat.pageDemandee) ecrire({ page: String(donnees.page) }, true);
  }, [donnees, etat.pageDemandee, ecrire]);

  const ouvrirFiche = useCallback(async (id: string) => {
    setDetail({ phase: 'chargement', id });
    const resultat = await lireClient(id);
    if (resultat.ok && resultat.donnees) {
      setDetail({ phase: 'pret', fiche: resultat.donnees });
    } else {
      setDetail({ phase: 'panne', id, message: resultat.message ?? 'Erreur inattendue.' });
    }
  }, []);

  const archiver = async () => {
    setErreurAction(undefined);
    const resultat = await archiverClients(selection);
    if (resultat.ok && resultat.donnees) {
      setConfirmation(
        `${resultat.donnees.archives} client(s) archivé(s) : ils quittent votre portefeuille, leurs interventions restent au registre.`,
      );
      setSelection([]);
      void charger(new AbortController().signal);
    } else {
      setErreurAction(resultat.message ?? 'Archivage refusé.');
    }
  };

  const effacerLesFiltres = () => {
    setSaisie('');
    setParametres(new URLSearchParams(), { replace: true });
  };

  /** Un en-tête de colonne triable : le tri est demandé au SERVEUR. */
  const enTeteTriable = (cle: TriClients, libelle: string, centre = false) => {
    const actif = tri === cle;
    const prochainSens: SensTri = actif && sens === 'asc' ? 'desc' : 'asc';
    const Icone = actif ? (sens === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th
        scope="col"
        className={`px-4 py-4 whitespace-nowrap ${centre ? 'text-center' : ''}`}
        aria-sort={actif ? (sens === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          onClick={() => ecrire({ tri: cle, sens: prochainSens })}
          className="inline-flex items-center gap-1 hover:text-slate-800"
        >
          {libelle}
          <Icone className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
        </button>
      </th>
    );
  };

  const resumeAffichage = useMemo(() => {
    if (!donnees) return '';
    if (donnees.total === 0) return 'Aucun client';
    const debut = (donnees.page - 1) * donnees.taille + 1;
    const fin = Math.min(debut + donnees.items.length - 1, donnees.total);
    const mot = donnees.total === 1 ? 'client' : 'clients';
    return `Affichage de ${debut} à ${fin} sur ${donnees.total} ${mot}`;
  }, [donnees]);

  const toutCocher = (cocher: boolean) => {
    setSelection(cocher ? (donnees?.items ?? []).map((client) => client.id) : []);
  };

  return (
    <div className="space-y-4">
      {/* Barre d'outils : recherche, filtres, vue, export, création */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3">
        <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
          <div className="relative w-full sm:w-72">
            <Search
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <label htmlFor="recherche-client" className="sr-only">
              Rechercher un client
            </label>
            <input
              id="recherche-client"
              type="search"
              value={saisie}
              onChange={(evenement) => setSaisie(evenement.target.value)}
              placeholder="Rechercher (nom, email, SIRET, ville)…"
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <label htmlFor="filtre-ville" className="sr-only">
            Filtrer par ville
          </label>
          <select
            id="filtre-ville"
            value={ville}
            onChange={(evenement) => ecrire({ ville: evenement.target.value })}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">Toutes les villes</option>
            {(filtres?.villes ?? []).map((valeur) => (
              <option key={valeur} value={valeur}>
                {valeur}
              </option>
            ))}
          </select>

          <label htmlFor="filtre-secteur" className="sr-only">
            Filtrer par secteur
          </label>
          <select
            id="filtre-secteur"
            value={secteur}
            onChange={(evenement) => ecrire({ secteur: evenement.target.value })}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">Tous les secteurs</option>
            {(filtres?.secteurs ?? []).map((valeur) => (
              <option key={valeur} value={valeur}>
                {valeur}
              </option>
            ))}
          </select>

          <label htmlFor="filtre-situation" className="sr-only">
            Filtrer par situation
          </label>
          <select
            id="filtre-situation"
            value={situation}
            onChange={(evenement) => ecrire({ situation: evenement.target.value })}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {OPTIONS_SITUATION.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>

          {filtresActifs && (
            <button
              type="button"
              onClick={effacerLesFiltres}
              className="px-3 py-2 border border-slate-300 text-slate-600 text-sm rounded-lg hover:bg-slate-50"
            >
              Réinitialiser
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
          {/* Bascule de vue : même donnée, deux présentations. */}
          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
            <button
              type="button"
              onClick={() => setVue('tableau')}
              aria-pressed={vue === 'tableau'}
              className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                vue === 'tableau' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
              }`}
            >
              <List className="w-3.5 h-3.5" aria-hidden="true" /> Tableau
            </button>
            <button
              type="button"
              onClick={() => setVue('cartes')}
              aria-pressed={vue === 'cartes'}
              className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                vue === 'cartes' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" aria-hidden="true" /> Cartes
            </button>
          </div>

          {/* Export : le fichier est fabriqué par le SERVEUR, filtres compris. */}
          <a
            href={urlExportClients({ recherche, situation, ville, secteur, tri, sens })}
            className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" aria-hidden="true" /> Exporter CSV
          </a>

          <button
            type="button"
            onClick={() => {
              setCreation(true);
              setConfirmation(undefined);
            }}
            aria-expanded={creation}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" aria-hidden="true" /> Nouveau client
          </button>
        </div>
      </div>

      {confirmation && (
        <p role="status" className="text-sm text-green-700">
          {confirmation}
        </p>
      )}
      {erreurAction && (
        <p role="alert" className="text-sm text-red-700">
          {erreurAction}
        </p>
      )}

      {/* Actions groupées : visibles dès qu'une ligne est cochée. */}
      {selection.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
            <Users className="w-4 h-4" aria-hidden="true" />
            {selection.length} client{selection.length > 1 ? 's' : ''} sélectionné
            {selection.length > 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            {/* Pas de bouton mort : l'envoi groupé n'est pas livré, et il le dit. */}
            <button
              type="button"
              disabled
              title="L’envoi d’emails groupés n’est pas encore livré : la messagerie est marquée « bientôt »."
              className="px-3 py-1.5 text-xs font-medium text-slate-400 bg-white border border-slate-200 rounded-lg opacity-70 cursor-not-allowed flex items-center gap-1"
            >
              <Mail className="w-3.5 h-3.5" aria-hidden="true" /> Email groupé
            </button>
            <button
              type="button"
              onClick={() => void archiver()}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" aria-hidden="true" /> Archiver
            </button>
          </div>
        </div>
      )}

      {creation && (
        <FormulaireClient
          secteurs={secteursPossibles}
          onEnregistrer={(saisieClient) => creerClient(saisieClient)}
          onReussi={(client) => {
            setCreation(false);
            setConfirmation(`Client « ${client.nom} » enregistré (${client.id}).`);
            void charger(new AbortController().signal);
          }}
          onFermer={() => setCreation(false)}
        />
      )}

      {/* Fiche détaillée : demandée au serveur, jamais recopiée de la ligne. */}
      {detail.phase !== 'ferme' && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" aria-live="polite">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              {detail.phase === 'chargement' && (
                <p className="text-sm text-slate-500">Chargement de la fiche…</p>
              )}

              {detail.phase === 'panne' && (
                <div role="alert">
                  <p className="font-medium text-red-700 mb-1">Fiche indisponible</p>
                  <p className="text-sm text-slate-600">
                    {detail.message}{' '}
                    <button
                      type="button"
                      onClick={() => void ouvrirFiche(detail.id)}
                      className="underline text-red-700"
                    >
                      Réessayer
                    </button>
                  </p>
                </div>
              )}

              {detail.phase === 'pret' && (
                <>
                  <h2 className="text-base font-semibold text-slate-800 mb-3">
                    {detail.fiche.client.nom} — {detail.fiche.client.id}
                  </h2>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {[
                      ['Contact', detail.fiche.client.contact],
                      ['Fonction', detail.fiche.client.fonction ?? '—'],
                      ['Courriel', detail.fiche.client.email],
                      ['Téléphone', detail.fiche.client.telephone || '—'],
                      ['SIRET', detail.fiche.client.siret ?? '—'],
                      ['Secteur', detail.fiche.client.secteur ?? '—'],
                      [
                        'Adresse',
                        [
                          detail.fiche.client.rue,
                          detail.fiche.client.codePostal,
                          detail.fiche.client.ville,
                        ]
                          .filter(Boolean)
                          .join(' ') || '—',
                      ],
                      ['Dernier passage', detail.fiche.client.dernierPassage ?? 'Aucun'],
                      ['Dernière activité', detail.fiche.client.activiteLibelle ?? 'Aucune'],
                    ].map(([libelle, valeur]) => (
                      <div key={libelle}>
                        <dt className="text-xs uppercase tracking-wide text-slate-400">
                          {libelle}
                        </dt>
                        <dd className="text-slate-700 break-words">{valeur}</dd>
                      </div>
                    ))}
                  </dl>
                  {detail.fiche.client.notes && (
                    <p className="mt-3 text-sm text-slate-600">
                      <span className="text-xs uppercase tracking-wide text-slate-400">
                        Notes internes
                      </span>
                      <br />
                      {detail.fiche.client.notes}
                    </p>
                  )}

                  <h3 className="text-xs uppercase tracking-wide text-slate-400 mt-4 mb-2">
                    Interventions de ce client
                  </h3>
                  {detail.fiche.interventions.length === 0 ? (
                    <p className="text-sm text-slate-600">
                      Aucune intervention enregistrée pour ce client.
                    </p>
                  ) : (
                    <table className="w-full text-sm text-left">
                      <caption className="sr-only">
                        Interventions de {detail.fiche.client.nom}, servies par le serveur
                      </caption>
                      <thead className="text-slate-500 font-medium border-b border-slate-200">
                        <tr>
                          <th scope="col" className="py-2 pr-6">
                            Date
                          </th>
                          <th scope="col" className="py-2 pr-6">
                            Type
                          </th>
                          <th scope="col" className="py-2 pr-6">
                            Lieu
                          </th>
                          <th scope="col" className="py-2">
                            Statut
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {detail.fiche.interventions.map((intervention) => (
                          <tr key={intervention.id}>
                            <td className="py-2 pr-6 whitespace-nowrap text-slate-700 mono">
                              {intervention.date}
                            </td>
                            <td className="py-2 pr-6 text-slate-600">
                              {intervention.typeIntervention}
                            </td>
                            <td className="py-2 pr-6 text-slate-600">{intervention.lieu}</td>
                            <td className="py-2">
                              <BadgeStatut statut={intervention.statut} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setDetail({ phase: 'ferme' })}
              aria-label="Fermer la fiche"
              className="p-1 rounded hover:bg-slate-100"
            >
              <X className="w-4 h-4 text-slate-500" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {etat.erreur && (
        <div className="bg-white rounded-xl border border-red-200 p-6" role="alert">
          <p className="font-medium text-red-700 mb-1">Liste indisponible</p>
          <p className="text-sm text-slate-600 mb-4">{etat.erreur}</p>
          <button
            type="button"
            onClick={() => void charger(new AbortController().signal)}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
          >
            Réessayer
          </button>
        </div>
      )}

      {!donnees && etat.chargement && !etat.erreur && (
        <p className="text-sm text-slate-500">Chargement des clients…</p>
      )}

      {donnees && donnees.items.length === 0 && !etat.erreur && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <p className="text-sm text-slate-600 mb-3">
            {filtresActifs
              ? 'Aucun client ne correspond à ces critères.'
              : 'Aucun client enregistré pour le moment.'}
          </p>
          {filtresActifs && (
            <button
              type="button"
              onClick={effacerLesFiltres}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
            >
              Effacer les filtres
            </button>
          )}
        </div>
      )}

      {donnees && donnees.items.length > 0 && vue === 'tableau' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div
            className="overflow-x-auto transition-opacity"
            aria-busy={etat.chargement}
            style={etat.chargement ? { opacity: 0.6 } : undefined}
          >
            <table className="w-full text-sm text-left">
              <caption className="sr-only">
                Clients du prestataire, filtrés, triés et paginés par le serveur
              </caption>
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                <tr>
                  <th scope="col" className="px-4 py-4 w-10">
                    <CaseTout
                      coches={selection.length}
                      total={donnees.items.length}
                      onChange={toutCocher}
                    />
                  </th>
                  {enTeteTriable('nom', 'Client')}
                  <th scope="col" className="px-4 py-4">
                    Contact
                  </th>
                  <th scope="col" className="px-4 py-4">
                    Localisation
                  </th>
                  {enTeteTriable('interventions', 'Interventions', true)}
                  {enTeteTriable('activite', 'Dernière activité')}
                  <th scope="col" className="px-4 py-4">
                    Situation
                  </th>
                  <th scope="col" className="px-4 py-4 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {donnees.items.map((client) => (
                  <LigneClient
                    key={client.id}
                    client={client}
                    coche={selection.includes(client.id)}
                    onCocher={(coche) =>
                      setSelection((precedente) =>
                        coche
                          ? [...precedente, client.id]
                          : precedente.filter((id) => id !== client.id),
                      )
                    }
                    onOuvrir={() => void ouvrirFiche(client.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-xs text-slate-500">{resumeAffichage}</span>
            <Pagination
              page={donnees.page}
              pages={donnees.pages}
              onChange={(numero) => ecrire({ page: String(numero) })}
            />
          </div>
        </div>
      )}

      {donnees && donnees.items.length > 0 && vue === 'cartes' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {donnees.items.map((client) => (
              <CarteClient
                key={client.id}
                client={client}
                onOuvrir={() => void ouvrirFiche(client.id)}
              />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">{resumeAffichage}</span>
            <Pagination
              page={donnees.page}
              pages={donnees.pages}
              onChange={(numero) => ecrire({ page: String(numero) })}
            />
          </div>
        </>
      )}
    </div>
  );
}

/** La case d'en-tête : cochée, vide, ou INDÉTERMINÉE quand une partie est cochée. */
function CaseTout({
  coches,
  total,
  onChange,
}: {
  readonly coches: number;
  readonly total: number;
  readonly onChange: (cocher: boolean) => void;
}) {
  const reference = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // `indeterminate` n'est pas un attribut HTML : il ne se pose qu'en DOM.
    if (reference.current) reference.current.indeterminate = coches > 0 && coches < total;
  }, [coches, total]);

  return (
    <input
      ref={reference}
      type="checkbox"
      aria-label="Tout sélectionner"
      checked={coches > 0 && coches === total}
      onChange={(evenement) => onChange(evenement.target.checked)}
      className="w-4 h-4 rounded border-slate-300"
    />
  );
}

/** Une ligne du tableau : tout vient du serveur, l'écran ne recompte rien. */
function LigneClient({
  client,
  coche,
  onCocher,
  onOuvrir,
}: {
  readonly client: ClientResume;
  readonly coche: boolean;
  readonly onCocher: (coche: boolean) => void;
  readonly onOuvrir: () => void;
}) {
  return (
    <tr className={`hover:bg-slate-50 transition-colors ${coche ? 'bg-blue-50' : ''}`}>
      <td className="px-4 py-4">
        <input
          type="checkbox"
          aria-label={`Sélectionner ${client.nom}`}
          checked={coche}
          onChange={(evenement) => onCocher(evenement.target.checked)}
          className="w-4 h-4 rounded border-slate-300"
        />
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${teinte(client.id)}`}
          >
            {initiales(client.nom)}
          </span>
          <div className="min-w-0">
            <p className="font-medium text-slate-800 truncate">{client.nom}</p>
            <p className="text-xs text-slate-500">
              {client.siret ? `SIRET : ${client.siret}` : (client.secteur ?? 'SIRET non renseigné')}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        <p className="text-slate-800">{client.contact}</p>
        <p className="text-xs text-slate-500 truncate">
          {client.fonction ? `${client.fonction} · ` : ''}
          {client.email}
        </p>
      </td>
      <td className="px-4 py-4 text-slate-600 whitespace-nowrap">
        {client.ville}
        {client.codePostal && (
          <span className="text-xs text-slate-400"> ({client.codePostal})</span>
        )}
      </td>
      <td className="px-4 py-4 text-center">
        <Link
          to={`/interventions?recherche=${encodeURIComponent(client.nom)}`}
          className="font-bold text-blue-600 hover:underline"
          title={`Voir les interventions de ${client.nom}`}
        >
          {client.interventions}
        </Link>
        {client.aVenir > 0 && (
          <span className="block text-[11px] text-slate-400">{client.aVenir} à venir</span>
        )}
      </td>
      <td className="px-4 py-4 text-xs text-slate-500 whitespace-nowrap">
        {client.activiteLibelle ?? '—'}
      </td>
      <td className="px-4 py-4">
        <BadgeSituation situation={client.situation} />
      </td>
      <td className="px-4 py-4 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={onOuvrir}
          className="text-red-600 hover:text-red-800 font-medium text-xs"
        >
          Fiche<span className="sr-only"> de {client.nom}</span>
        </button>
      </td>
    </tr>
  );
}

/** La même ligne, en carte : aucune donnée supplémentaire n'est calculée ici. */
function CarteClient({
  client,
  onOuvrir,
}: {
  readonly client: ClientResume;
  readonly onOuvrir: () => void;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:border-red-300 transition-colors">
      <div className="flex justify-between items-start mb-4">
        <span
          aria-hidden="true"
          className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${teinte(client.id)}`}
        >
          {initiales(client.nom)}
        </span>
        <BadgeSituation situation={client.situation} />
      </div>
      <h3 className="font-bold text-slate-800 text-base mb-1 truncate">{client.nom}</h3>
      <p className="text-xs text-slate-500 mb-4 flex items-center gap-1">
        <MapPin className="w-3 h-3" aria-hidden="true" />
        {client.ville}
        {client.codePostal ? ` (${client.codePostal})` : ''}
      </p>

      <div className="space-y-2 mb-4 text-sm">
        <p className="text-slate-600 truncate">{client.contact}</p>
        <p className="text-xs text-slate-500 truncate">{client.email}</p>
        <p className="text-slate-600">
          {client.interventions} intervention{client.interventions > 1 ? 's' : ''}
          {client.activiteLibelle ? ` · ${client.activiteLibelle}` : ''}
        </p>
      </div>

      <div className="pt-4 border-t border-slate-100 flex gap-2">
        <button
          type="button"
          onClick={onOuvrir}
          className="flex-1 py-1.5 text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded hover:bg-slate-100"
        >
          Détails<span className="sr-only"> de {client.nom}</span>
        </button>
        <Link
          to={`/interventions?recherche=${encodeURIComponent(client.nom)}`}
          className="flex-1 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-100 rounded hover:bg-red-100 text-center"
        >
          Interventions
        </Link>
      </div>
    </div>
  );
}
